import React from 'react';
import classnames from 'classnames';

import {type AriaTabPanelProps, useTab, useTabList, useTabPanel} from '@react-aria/tabs';
import {useTabListState} from '@react-stately/tabs';
import {Item as CollectionItem} from '@react-stately/collections';
import type {CollectionChildren, Node, ItemProps} from '@react-types/shared';
import type {TabListState, TabListProps} from '@react-stately/tabs';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {ShowMore} from './show_more';

import styles from './styles.module.css';

const DEFAULT_MAX_TABS_VISIBLE = 6;

export interface TabItemProps {
    allowTabNavigation?: boolean;
    item: Node<Record<string, string>>;
    dot?: boolean;
    state: TabListState<Record<string, string>>;
    className?: string;
}

const TabItem: React.FC<TabItemProps> = ({item, state, dot, className, allowTabNavigation}) => {
    const {key, rendered} = item;
    const ref = React.useRef(null);
    const {tabProps, isSelected} = useTab({key}, state, ref);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!allowTabNavigation) {
            return;
        }

        if (event.key === 'Tab') {
            const isShiftHeld = event.shiftKey;
            const nextTabKey = isShiftHeld ? state.collection.getKeyBefore(key) : state.collection.getKeyAfter(key);

            if (nextTabKey) {
                event.stopPropagation();
                event.preventDefault();
                state.selectionManager.setFocusedKey(nextTabKey);
            }
        }
    };

    const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
        tabProps.onFocus?.(event);

        if (allowTabNavigation) {
            state.setSelectedKey(key);
        }
    };

    return (
        <div
            {...tabProps}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            tabIndex={allowTabNavigation ? 0 : tabProps.tabIndex}
            ref={ref}
            className={classnames(className, {[styles.active]: isSelected})}
        >
            <div className={styles.dot_container}>
                {rendered}
                {dot && !isSelected && <div className={styles.dot} />}
            </div>
        </div>
    );
};

export type TabPanelProps = {
    state: TabListState<Record<string, string>>;
    className?: string;
} & AriaTabPanelProps;

const TabPanel = ({state, className, ...props}: TabPanelProps) => {
    const ref = React.useRef(null);
    const {tabPanelProps} = useTabPanel(props, state, ref);

    return (
        <div
            data-test-id='searchResults'
            {...tabPanelProps}
            tabIndex={-1}
            className={className}
            ref={ref}
        >
            {state.selectedItem?.props.children}
        </div>
    );
};

export type TabsProps = {
    allowTabNavigation?: boolean;
    children: CollectionChildren<Record<string, string>>;
    dots?: React.Key[];
    maxTabsVisible?: number;
    moreLabel?: string;
    className?: string;
    tabPanelClassName?: string;
} & TabListProps<Record<string, string>>;

const Tabs: React.FC<TabsProps> = ({
    allowTabNavigation,
    maxTabsVisible = DEFAULT_MAX_TABS_VISIBLE,
    moreLabel,
    dots,
    className,
    tabPanelClassName,
    ...props
}) => {
    const tabTypography = useTypography({size: TypographySize.BodyM});
    const state = useTabListState(props);
    const ref = React.useRef(null);
    const {tabListProps} = useTabList(props, state, ref);

    const extraTabsCount = state.collection.size - maxTabsVisible;

    return (
        <div>
            <div data-test-id='tabs' className={classnames(styles.tabs, className)}>
                <div
                    {...tabListProps}
                    className={classnames(styles.tab_buttons)}
                    ref={ref}
                >
                    {Array.from(state.collection)
                        .slice(0, maxTabsVisible)
                        .map((item) => (
                            <TabItem
                                allowTabNavigation={allowTabNavigation}
                                key={item.key}
                                dot={dots?.includes(item.key)}
                                className={classnames(styles.row_tab_button, tabTypography)}
                                item={item}
                                state={state}
                            />
                        ))}
                </div>
                {extraTabsCount > 0 && moreLabel && (
                    <ShowMore
                        state={state}
                        moreLabel={moreLabel}
                        count={extraTabsCount}
                    />
                )}
            </div>
            <TabPanel
                className={tabPanelClassName}
                key={state.selectedItem?.key}
                state={state}
            />
        </div>
    );
};

const Item = CollectionItem as React.FC<Omit<ItemProps<unknown>, 'children'>>;

export {Item};

export default Tabs;
